import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, formatMoney, formatDate } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, X, CheckCircle2, Trash2, Pencil, RepeatIcon,
  Zap, StopCircle,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { IncomeModal } from "@/components/IncomeModal";

// Money Coming In — split into three unambiguous sections:
//   1. Recurring income  — pay schedules (weekly/biweekly/monthly). Editing here
//      changes future occurrences everywhere: pay-period cards, Cash-Flow
//      Forecast, monthly snapshots. Setting an `end_date` stops future
//      occurrences without deleting the row (audit trail preserved).
//   2. One-time income   — extras (bonus, tax refund, side gig). Bug-fixed:
//      these now correctly land in whichever pay-period card contains their
//      date, previously they were silently dropped from forecast math.
//   3. Received income   — history of paychecks marked received. Collapsible.

const FREQ_LABEL = { weekly: "Weekly", biweekly: "Biweekly", monthly: "Monthly", unscheduled: "One-time" };

export default function Income() {
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [endRecurrenceTarget, setEndRecurrenceTarget] = useState(null);
  // Deep-link support: /app/income?add=1 auto-opens the Add-income modal so
  // Dashboard "Add expected income" buttons land users straight in the form
  // without an extra click. Consumed once, then cleared from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setEditing(null);
      setModal(true);
      // Strip the param so a page refresh doesn't reopen the modal.
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/incomes");
    setIncomes(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const applyUpdated = (updated) => {
    setIncomes((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
  };

  const markReceived = async (inc) => {
    const { data } = await api.patch(`/incomes/${inc.id}`, { status: "received" });
    applyUpdated(data);
    toast.success(`Marked ${inc.source} received`);
  };
  const remove = async (inc) => {
    const backup = { ...inc };
    setIncomes((prev) => prev.filter((i) => i.id !== inc.id));
    try {
      await api.delete(`/incomes/${inc.id}`);
      toast(`Deleted "${inc.source}"`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data } = await api.post("/incomes", {
                source: backup.source, amount: backup.amount,
                expected_date: backup.expected_date, frequency: backup.frequency,
                status: backup.status, notes: backup.notes || null,
                end_date: backup.end_date || null,
              });
              setIncomes((prev) => [...prev, data]);
              toast.success("Restored");
            } catch (e) { toast.error(formatApiError(e)); }
          },
        },
      });
    } catch (e) {
      setIncomes((prev) => [...prev, backup]);
      toast.error(formatApiError(e));
    }
  };
  const endRecurrence = async (inc, endDate) => {
    // Present a controlled AlertDialog instead of a native browser confirm.
    setEndRecurrenceTarget({ inc, endDate });
  };
  const confirmEndRecurrence = async () => {
    if (!endRecurrenceTarget) return;
    const { inc, endDate } = endRecurrenceTarget;
    setEndRecurrenceTarget(null);
    try {
      const { data } = await api.post(`/incomes/${inc.id}/end-recurrence`, { end_date: endDate });
      applyUpdated(data);
      toast.success(`Ended ${inc.source} on ${formatDate(endDate)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const clearEndDate = async (inc) => {
    try {
      const { data } = await api.patch(`/incomes/${inc.id}`, { end_date: "" });
      applyUpdated(data);
      toast.success(`Removed end date for ${inc.source}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (inc) => { setEditing(inc); setModal(true); };
  const openAdd = () => { setEditing(null); setModal(true); };

  // Recurring rows: ANY row with a recurring frequency shows up here,
  // regardless of status. This is important for legacy zero-amount series
  // where a row's status may not be exactly "expected" but its occurrences
  // still get projected into future pay periods (see forecast.py). Without
  // this, users have no surface to delete them from.
  const recurring = incomes.filter((i) => i.frequency !== "unscheduled" && i.frequency);
  const oneTime = incomes.filter((i) => i.frequency === "unscheduled" && i.status === "expected");

  // "Received this month" summary — sums every income row whose
  // received_date lands in the current calendar month. Gives users an
  // at-a-glance answer to "how much has actually landed vs projected?"
  // without having to run reconciliation reports.
  const receivedThisMonth = (() => {
    const now = new Date();
    const y = now.getFullYear(); const m = now.getMonth();
    let total = 0; let count = 0;
    for (const i of incomes) {
      const rd = i.received_date || (i.status === "received" ? i.expected_date : null);
      if (!rd) continue;
      const d = new Date(rd + "T00:00:00");
      if (d.getFullYear() === y && d.getMonth() === m) {
        total += Number(i.amount) || 0;
        count += 1;
      }
    }
    return { total, count, monthLabel: now.toLocaleDateString(undefined, { month: "long", year: "numeric" }) };
  })();

  return (
    <div className="space-y-8" data-testid="income-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Manage Income Sources</div>
        <h1 className="font-heading text-3xl font-medium text-[#2C302E] mt-1">Money coming in</h1>
        <p className="text-[#59605C] mt-1 max-w-2xl">
          Any change here — new schedule, updated amount, end date, or one-off — instantly
          reshapes your Dashboard pay-period cards, Cash-Flow Forecast, and monthly snapshots.
        </p>
        <button className="btn-primary mt-4" onClick={openAdd} data-testid="income-add-button">
          <Plus size={16} /> Add income
        </button>
      </div>

      {/* Received-this-month strip — small callout above the recurring list. */}
      {!loading && (
        <div className="card-soft p-4 bg-[#EEF3EC] border border-[#B8CFB1] flex items-center gap-3" data-testid="income-received-this-month">
          <div className="w-10 h-10 rounded-full bg-[#3B5B3E] text-white flex items-center justify-center shrink-0">
            <CheckCircle2 size={18} className="stroke-[2.5]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#3B5B3E]">Landed this month</div>
            <div className="font-heading text-xl font-semibold text-[#2F4E32]" data-testid="income-received-total">
              {receivedThisMonth.count === 0
                ? "Nothing has landed yet"
                : `+${formatMoney(receivedThisMonth.total)} received`}
            </div>
            <div className="text-[11px] text-[#3B5B3E]/80">
              {receivedThisMonth.count > 0
                ? `${receivedThisMonth.count} paycheck${receivedThisMonth.count === 1 ? "" : "s"} confirmed in ${receivedThisMonth.monthLabel}`
                : `Confirmed paychecks in ${receivedThisMonth.monthLabel} will show up here as you mark them received.`}
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="text-[#59605C]">Loading…</div> : (
        <>
          <RecurringSection incomes={recurring}
            onEdit={openEdit} onDelete={remove} onEndRecurrence={endRecurrence}
            onClearEndDate={clearEndDate} onReceived={markReceived} />
          <OneTimeSection incomes={oneTime}
            onEdit={openEdit} onDelete={remove} onReceived={markReceived} />
          {/* Received income history — intentionally removed for now.
              Future: per-source pay history views, total received to date,
              variance vs expected, etc. See PRD.md backlog. */}
        </>
      )}

      {modal && (
        <IncomeModal
          income={editing}
          onClose={() => setModal(false)}
          onSaved={(updated) => {
            setModal(false);
            if (editing) applyUpdated(updated);
            else setIncomes((prev) => [...prev, updated]);
          }}
        />
      )}

      <AlertDialog
        open={endRecurrenceTarget !== null}
        onOpenChange={(open) => { if (!open) setEndRecurrenceTarget(null); }}
      >
        <AlertDialogContent data-testid="income-end-recurrence-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              End recurring income &ldquo;{endRecurrenceTarget?.inc?.source}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Future pay-period cards will stop showing this source after{" "}
              <span className="font-semibold text-[#2C302E]">
                {endRecurrenceTarget?.endDate ? formatDate(endRecurrenceTarget.endDate) : ""}
              </span>
              . The recurring row stays on this page (marked ended) so your history stays intact.
              You can clear the end date later to resume the schedule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="income-end-recurrence-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmEndRecurrence}
              data-testid="income-end-recurrence-confirm"
              className="bg-[#8C3A30] text-white hover:bg-[#6E2E27]"
            >
              End recurring
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------- Sub-components ----------------------
function RecurringSection({ incomes, onEdit, onDelete, onEndRecurrence, onClearEndDate, onReceived }) {
  const todayIso = new Date().toISOString().slice(0, 10);
  return (
    <div data-testid="income-recurring-section">
      <div className="flex items-baseline gap-3 mb-4 pb-2 border-b-2 border-[#4A6148]/25">
        <RepeatIcon size={20} className="text-[#4A6148] self-center" />
        <h2 className="font-heading text-2xl font-semibold text-[#4A6148]">Recurring income</h2>
        <span className="text-sm text-[#8A918D]">Auto-projects into every future pay period</span>
      </div>
      {incomes.length === 0 ? (
        <div className="card-soft p-6 text-sm text-[#59605C]">
          No recurring income set up yet. Add your paycheck, benefit deposit, or any other regular
          money coming in — Cash Buddy will project it into every future pay period.
        </div>
      ) : (
        <div className="card-soft !p-0 overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-[#8A918D] bg-[#F1EEE7]">
            <div className="col-span-3">Income Source</div>
            <div className="col-span-2 text-right">Amount</div>
            <div className="col-span-2">Frequency</div>
            <div className="col-span-2">Start Date</div>
            <div className="col-span-2">End Date</div>
            <div className="col-span-1" />
          </div>
          <div className="divide-y divide-[#EBE6E0]">
            {incomes.map((i) => (
              <div key={i.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center" data-testid={`income-recurring-row-${i.id}`}>
                <div className="col-span-12 md:col-span-3 min-w-0">
                  <div className="font-medium text-[#2C302E] truncate">{i.source}</div>
                  {i.notes && <div className="text-xs text-[#8A918D] truncate">{i.notes}</div>}
                </div>
                <div className="col-span-6 md:col-span-2 md:text-right font-semibold text-[#3B5B3E]">+{formatMoney(i.amount)}</div>
                <div className="col-span-6 md:col-span-2 text-sm text-[#59605C]">{FREQ_LABEL[i.frequency] || i.frequency}</div>
                <div className="col-span-6 md:col-span-2 text-sm text-[#59605C]">{formatDate(i.expected_date)}</div>
                <div className="col-span-6 md:col-span-2 text-sm">
                  {i.end_date ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F5D9D1] text-[#7A2E24] text-xs">
                      <StopCircle size={11} /> {formatDate(i.end_date)}
                    </span>
                  ) : <span className="text-[#8A918D] italic">ongoing</span>}
                </div>
                <div className="col-span-12 md:col-span-1 flex items-center justify-end gap-1">
                  <button className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onReceived(i)}
                    title="Mark most recent occurrence received"
                    data-testid={`income-mark-received-${i.id}`}>
                    <CheckCircle2 size={13} />
                  </button>
                  <button className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onEdit(i)}
                    data-testid={`income-edit-${i.id}`}>
                    <Pencil size={13} />
                  </button>
                  {i.end_date ? (
                    <button className="btn-ghost !px-2 !py-1 text-xs text-[#4A6148]" onClick={() => onClearEndDate(i)}
                      title="Remove end date — this schedule continues"
                      data-testid={`income-clear-end-${i.id}`}>
                      <RepeatIcon size={13} />
                    </button>
                  ) : (
                    <button className="btn-ghost !px-2 !py-1 text-xs text-[#8C5D30]" onClick={() => onEndRecurrence(i, todayIso)}
                      title="End this recurring series (last date will be today)"
                      data-testid={`income-end-recurrence-${i.id}`}>
                      <StopCircle size={13} />
                    </button>
                  )}
                  <button className="btn-ghost !px-2 !py-1" onClick={() => onDelete(i)}
                    aria-label="Delete" data-testid={`income-del-${i.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OneTimeSection({ incomes, onEdit, onDelete, onReceived }) {
  return (
    <div data-testid="income-onetime-section">
      <div className="flex items-baseline gap-3 mb-4 pb-2 border-b-2 border-[#8C5D30]/25">
        <Zap size={20} className="text-[#8C5D30] self-center" />
        <h2 className="font-heading text-2xl font-semibold text-[#8C5D30]">One-time income</h2>
        <span className="text-sm text-[#8A918D]">Bonuses, tax refunds, side gigs — appears once</span>
      </div>
      {incomes.length === 0 ? (
        <div className="card-soft p-6 text-sm text-[#59605C]">
          No one-off income logged. Add one whenever you sell something, get a rebate, or receive a bonus —
          it will land in whichever pay period contains that date.
        </div>
      ) : (
        <div className="card-soft !p-0 overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-[#8A918D] bg-[#F1EEE7]">
            <div className="col-span-3">Income Source</div>
            <div className="col-span-2 text-right">Amount</div>
            <div className="col-span-2">Frequency</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1" />
          </div>
          <div className="divide-y divide-[#EBE6E0]">
            {incomes.map((i) => (
              <div key={i.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center" data-testid={`income-onetime-row-${i.id}`}>
                <div className="col-span-12 md:col-span-3 min-w-0">
                  <div className="font-medium text-[#2C302E] truncate">{i.source}</div>
                  {i.notes && <div className="text-xs text-[#8A918D] truncate">{i.notes}</div>}
                </div>
                <div className="col-span-6 md:col-span-2 md:text-right font-semibold text-[#3B5B3E]">+{formatMoney(i.amount)}</div>
                <div className="col-span-6 md:col-span-2 text-sm text-[#59605C]">One-time</div>
                <div className="col-span-6 md:col-span-2 text-sm text-[#59605C]">{formatDate(i.expected_date)}</div>
                <div className="col-span-6 md:col-span-2 text-sm">
                  {i.status === "received" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E] text-xs">
                      <CheckCircle2 size={11} /> Received
                    </span>
                  ) : (
                    <span className="text-[#8A918D] italic">Expected</span>
                  )}
                </div>
                <div className="col-span-12 md:col-span-1 flex items-center justify-end gap-1">
                  {i.status !== "received" && (
                    <button className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onReceived(i)}
                      title="Mark as received"
                      data-testid={`income-onetime-received-${i.id}`}>
                      <CheckCircle2 size={13} />
                    </button>
                  )}
                  <button className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onEdit(i)}
                    data-testid={`income-onetime-edit-${i.id}`}>
                    <Pencil size={13} />
                  </button>
                  <button className="btn-ghost !px-2 !py-1" onClick={() => onDelete(i)}
                    aria-label="Delete" data-testid={`income-onetime-del-${i.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
